import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNewTranslation } from 'hooks'
import { theme } from 'theme'
import { BaseEditorContainer } from '../PredefinedTaskEditor/styles'
import Divider from 'components/Shared/Divider'
import Text from 'particles/Text'
import InputField from 'components/Shared/Fields'
import { FormikRadioButtonGroup } from 'particles/RadioButton/Group'
import { InputFieldContainer } from 'components/Shared/Fields/styles'
import styled from 'particles/themes/styled'
import { useFormikContext } from 'formik'
import { EditorValues } from './types'
import { useSelector } from 'react-redux'
import { selectIsEngagementStepTypeHelper, selectTaskPlatformContentTypeOptions } from 'state/constants'
import { PLATFORM_CONTENT_TYPE_ENUM } from 'services/api/constants'
import ConfirmationModal from 'components/Shared/ConfirmationModal'
import Icon from 'components/Icon'
import { Box } from 'rebass'
import { CAMPAIGN_STATUS_ENUM, selectEditingCampaign } from 'state/campaigns'
import _ from 'lodash'
import CampaignEditorInputFieldContainer from 'components/Shared/Permission/CampaignEditorInputFieldContainer'
import CommentContainer from 'components/Shared/Comment'
import Label from 'particles/Label'
import { fieldIdentifiers } from 'components/Shared/Comment'

type CustomTaskSetupEditor = {
    className?: string
    onConfirmDeletingEngagementMetric: VoidFunction
    taskUuid: string
}

const TaskSetupEditor: React.FC<CustomTaskSetupEditor> = ({
    className,
    onConfirmDeletingEngagementMetric,
    taskUuid,
}) => {
    const [t] = useNewTranslation()
    const platformContentTypeOptions = useSelector(selectTaskPlatformContentTypeOptions)
    const isEngagementStepType = useSelector(selectIsEngagementStepTypeHelper)
    const formikBag = useFormikContext<EditorValues>()
    const editingCampaign = useSelector(selectEditingCampaign)
    const isCampaignDraftOrForEdit =
        editingCampaign && [CAMPAIGN_STATUS_ENUM.DRAFT, CAMPAIGN_STATUS_ENUM.FOR_EDIT].includes(editingCampaign?.status)
    const isCampaignLive = Boolean(editingCampaign && editingCampaign.status === CAMPAIGN_STATUS_ENUM.LIVE)
    const [showConfirmation, setShowConfirmation] = useState(false)
    const handleCloseConfirmation = useCallback(() => {
        setShowConfirmation(false)
        formikBag.setFieldValue('platform_content_type', formikBag.initialValues.platform_content_type)
    }, [formikBag.initialValues.platform_content_type])
    const [confirmed, setConfirmed] = useState(false)
    const handleConfirmation = useCallback(async () => {
        formikBag.setValues(values => {
            return {
                ...values,
                steps: values.steps.filter(step => !isEngagementStepType(step.type)),
                payouts: {
                    // clear payout engagement field
                    ..._.pick(values.payouts, ['base', 'extras']),
                    engagementMetrics: [],
                },
            }
        })
        // we need to trigger re render first by setting setConfirmed,
        // then call formik.submitForm inside useEffect below triggered by the confirmed state change
        // this is to ensure that formikBag.setValues has finished change the formikBag.values
        setConfirmed(true)
    }, [isEngagementStepType])
    useEffect(() => {
        if (confirmed) {
            onConfirmDeletingEngagementMetric()
            setConfirmed(false)
        }
        setShowConfirmation(false)
    }, [confirmed])

    const otherFieldRef = useRef<HTMLInputElement>(null)
    const onPlatformContentTypeChanged = useCallback(
        (value: PLATFORM_CONTENT_TYPE_ENUM) => {
            if (
                formikBag.values.steps.some(step => isEngagementStepType(step.type)) &&
                formikBag.initialValues.platform_content_type &&
                value !== formikBag.initialValues.platform_content_type
            ) {
                setShowConfirmation(true)
            }

            if (value !== PLATFORM_CONTENT_TYPE_ENUM.OTHERS) {
                formikBag.setFieldValue('platform_content_type_others', null, false)
                return
            }

            formikBag.setFieldValue(
                'platform_content_type_others',
                formikBag.initialValues.platform_content_type_others,
            )
            setTimeout(() => {
                if (otherFieldRef.current) {
                    otherFieldRef.current.scrollIntoView(true)
                    otherFieldRef.current.focus()
                }
            }, 0)
        },
        [
            formikBag.initialValues.platform_content_type_others,
            formikBag.initialValues.platform_content_type,
            formikBag.values.steps,
            isEngagementStepType,
        ],
    )

    return (
        <BaseEditorContainer className={className}>
            <Text scale="H4" color="primary">
                {t('campaignEditor.createTask.createCustomTaskTitle')}
            </Text>
            <Divider maxWidth="1006px" className="first-divider" />
            <div className="title-input-field">
                <CampaignEditorInputFieldContainer>
                    <InputField.TextInputField
                        className={'input-area'}
                        label={t('campaignEditor.createTask.enterTitleHeader')}
                        placeholder={t('taskType.customTask')}
                        name="title"
                        showInputLength
                        maxInputLength={150}
                        isRequired
                        width="100%"
                        identifier={`${taskUuid}_${fieldIdentifiers.setupTitle}`}
                    />
                    <InputField.TextAreaField
                        label={t('campaignEditor.createTask.enterDescriptionHeader')}
                        name="description"
                        width="100%"
                        minTextAreaHeight={112}
                        identifier={`${taskUuid}_${fieldIdentifiers.setupDescription}`}
                    />
                </CampaignEditorInputFieldContainer>

                <InputFieldContainer>
                    <CommentContainer identifier={`${taskUuid}_${fieldIdentifiers.setupTaskType}`}>
                        <Label title={t('campaignEditor.customAndRegistrationField.taskTypeHeader')} />
                    </CommentContainer>
                    <Box height={theme.newSpacing.xxs} />
                    <CampaignEditorInputFieldContainer>
                        <FormikRadioButtonGroup
                            name="platform_content_type"
                            options={platformContentTypeOptions}
                            onClick={onPlatformContentTypeChanged}
                            isReadOnly={isCampaignLive ? isCampaignLive : undefined}
                            isDisabled={
                                !isCampaignDraftOrForEdit && formikBag.initialValues.platform_content_type !== null
                            }
                        />
                    </CampaignEditorInputFieldContainer>
                    <div
                        style={{
                            display:
                                formikBag.values.platform_content_type === PLATFORM_CONTENT_TYPE_ENUM.OTHERS
                                    ? 'block'
                                    : 'none',
                        }}
                    >
                        <CampaignEditorInputFieldContainer>
                            <InputField.TextInputField
                                className={'input-area'}
                                name="platform_content_type_others"
                                subtext={t('campaignEditor.customAndRegistrationField.enterTaskTypeSubtext') + ' *'}
                                ref={otherFieldRef}
                                width={'100%'}
                                showInputLength
                                maxInputLength={100}
                                isReadOnly={isCampaignLive}
                                isDisabled={
                                    !isCampaignDraftOrForEdit && formikBag.initialValues.platform_content_type !== null
                                }
                            />
                        </CampaignEditorInputFieldContainer>
                    </div>
                </InputFieldContainer>
            </div>
            <ConfirmationModal
                open={showConfirmation}
                toggle={handleCloseConfirmation}
                onConfirm={handleConfirmation}
                width="440px"
                mainIcon={<Icon.WarningCircleIcon size={'40px'} />}
                confirmText={t('button.changeNow')}
                cancelText={t('button.noGoBack')}
            >
                <Text scale="H3" color="primary" width="100%">
                    {t('modalWindow.confirmationMessage.changeTaskTypeTitle')}
                </Text>
                <Box height={theme.newSpacing.xxs} />
                <Text scale="bodyM400" color="secondary" width="100%">
                    {t('modalWindow.confirmationMessage.changeTaskTypeSubtitle')}
                </Text>
            </ConfirmationModal>
        </BaseEditorContainer>
    )
}

export default styled(TaskSetupEditor)`
    background: ${theme.newColors.neutral50};
    height: fit-content;

    .title-input-field {
        display: flex;
        flex-direction: column;
        gap: ${theme.newSpacing.l};
    }
`
